/* global BigInt */
import { constants } from "./general";

export const buildEmptyChallenge = (mcMode = false) => {
  const challenge = {};
  challenge.difficulty = 1;
  challenge.standards = null;
  challenge.comments = null;
  challenge.status = "active";
  challenge.prompt = "";
  challenge.spoken_prompt = null;
  challenge.solution_index = null;
  challenge.answer_options = mcMode ? [] : null;
  challenge.randomize_options = false;
  challenge.prompt_image_url = null;
  challenge.render_type = "fraction_legacy";
  challenge.source = "LEGACY";
  challenge.external_id = null;
  return challenge;
};

export const buildPreviewChallenge = (challenge) => {
  const previewChallenge = JSON.parse(JSON.stringify(challenge));
  const newAnswerOptions = [];
  previewChallenge?.answer_options?.forEach((option, index) => {
    newAnswerOptions[index] = {text: option, eligible: true};
  });

  if (newAnswerOptions.length) {
      previewChallenge.answer_options = newAnswerOptions;
  }
  
  return previewChallenge;
};

export const buttonIdsToStr = (buttonIds) => {
  if (buttonIds) {
    return buttonIds.map((i) => i.toString());
  }
  return [];
};

export const handleSaveChallengeError = (activity, errorDetails) => {
  if (Array.isArray(errorDetails)) {
    let relatedErrors = [];
    if (activity === constants.CONSENSUS_ENUM) {
      errorDetails.forEach((i) => {
        if (JSON.stringify(i).indexOf("MultipleChoice") > -1) {
          relatedErrors.push(i);
        }
      });
      return relatedErrors;
    } else if (activity === constants.CRAZY_CALCULATOR_ENUM) {
      errorDetails.forEach((i) => {
        if (JSON.stringify(i).indexOf("CrazyCalculator") > -1) {
          relatedErrors.push(i);
        }
      });
      return relatedErrors;
    }
  }
  return errorDetails;
};

export const CONSENSUS_VARIANT_OPTIONS = [
  { value: 'multiplechoice', label: 'Multiple Choice' },
  { value: 'openresponse', label: 'Open Response' }
];

export const CHALLENGE_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  ...CONSENSUS_VARIANT_OPTIONS,
];

// Illustration filter. Base options plus a per-type option for each
// illustration type the current results carry (e.g. "graph", "geometry"),
// so it adapts to whatever a provider sends. "has"/"none" map to the
// has_image param; "type:<t>" maps to the illustration_type param.
export const ILLUSTRATION_BASE_OPTIONS = [
  { value: "all", label: "All illustrations" },
  { value: "has", label: "Has image" },
  { value: "none", label: "No image" },
];

export const getIllustrationOptions = (illustrationTypes = []) => {
  const typeOptions = [...illustrationTypes]
    .sort()
    .map((t) => ({ value: `type:${t}`, label: `Image: ${t}` }));
  return [...ILLUSTRATION_BASE_OPTIONS, ...typeOptions];
};

export const matchesIllustration = (challenge, illustrationFilter) => {
  if (!illustrationFilter || illustrationFilter === "all") return true;
  const hasImage = Boolean(challenge.illustration) ||
    Boolean(challenge.internal_image_url && challenge.internal_image_url !== "nan");
  if (illustrationFilter === "has") return hasImage;
  if (illustrationFilter === "none") return !hasImage;
  if (illustrationFilter.startsWith("type:")) {
    return challenge.illustration_taxonomy?.wren_illustration_type === illustrationFilter.slice(5);
  }
  return true;
};

export const CHALLENGE_QUALITY_LLM_PROMPT = "You will receive a list of challenges, where each challenge can be " +
  "one of the 2 types: MultipleChoice or OpenResponse.\nMultipleChoice challenge always has 'answer_options' and " +
  "'solution_index' props, and never has 'solution' prop.\nMultipleChoice challenge has only one correct answer " +
  "option which is referenced in 'solution_index' so the value of 'solution_index' is a number of an element in " +
  "'answer_options' array.\nOpenResponse challenges always have 'solution' prop and never have 'answer_options' or " +
  "'solution_index' props.\nThe question for each challenge is in 'prompt' prop.\nThere could be also 'internal_image_url' " +
  "prop that has some graphical content related to the question.\nIf it's there, a base64 encoded image will be " +
  "appended to the message with the challenge.\nIgnore 'prompt_image_url' prop as it's not used.\nYour task is to " +
  "analyze every challenge and its image, and flag any challenges that are either confusing, badly worded, badly formatted, have wrong expected solution, " +
  "don't have enough context (like there is no 'internal_image_url', hence no attached base64 image, but the challenge " +
  "'prompt' asks to provide an answer for some missing context), are in any language other than English, the attached image " +
  "is not legible, blurry, or not centered correctly, etc.\nRespond with a JSON-formatted list of challenge ids ('id' prop from the request).\n" +
  "Include only those challenges that were flagged.\nInclude the reason for flagging in a new 'flagged_reason' prop " +
  "for each challenge.\nReturn an empty array if there were no flagged challenges.\nDon't include any chars like ` in " +
  "the response as they could make JSON serialization for later needs impossible.";

export const CHALLENGE_EXPLANATION_LLM_PROMPT = "Your task is to provide a response that will be a JSON object " +
  "with exactly 2 keys: 'explanation' and 'image_prompt'. The 'explanation' key's value should be an array of strings, and the " +
  "'image_prompt' key's value should be a string.\n The explanation array could consist of 1 or more strings. Those strings should provide an " +
  "explanation for a math challenge so that an elementary school student can understand it. The image prompt will be" +
  "used in a separate follow-up image generation LLM request.\nInclude as many details as possible in the image " +
  "generation prompt, so it correlates with the explanation text as close as possible.\nThe math challenge can be " +
  "one of 2 types: MultipleChoice or OpenResponse.\nMultipleChoice challenge always has 'answer_options' and " +
  "'solution_index' props, and never has 'solution' prop.\nMultipleChoice challenge has only one correct " +
  "answer option which is referenced in 'solution_index' so the value of 'solution_index' is a number of an element " +
  "in 'answer_options' array.\nOpenResponse challenge always has 'solution' prop and never has 'answer_options' or " +
  "'solution_index' props. The question for each challenge is in 'prompt' prop.\nThere could also be 'internal_image_url' " +
  "prop that has some graphical content related to the question. It could be empty, meaning the current challenge " +
  "consists only of text.\nProperty 'standards' is the list of standard codes the challenge is aligned with.\n" +
  "The first character of the standard code is the grade level, where K is Kindergarten, 1 is 1st grade, etc.\n" +
  "Always generate an explanation that is intuitive and easy to understand by a student of that grade level.\n" +
  "The serialized math challenge object will be provided below.\nAn example of a challenge and its explanation with an input image can be " +
  "included in the prompt context as well. If so, use those as an example when generating the response.\nThe explanation " +
  "could use a lot of abstraction and analogies if needed to explain the solution while referring to a comparison " +
  "that can be unrelated to the exact details that are mentioned in the challenge, but should correctly explain " +
  "the solution.\nThe explanation should be generated with an assumption that it will be used " +
  "along with an image that will be generated by an LLM in a separate request.\nThe explanation text should be short " +
  "and to the point.\nThe image prompt should mention that the image should be more of a sketch or a diagram that " +
  "explains a solution for a math problem with minimal amount of details, rather than a painting or a photo with " +
  "great amount of details.\nDon't include any requests to draw calculations or numbers in the image prompt.\n" +
  "Don't include any requests to draw characters, backgrounds, etc. in the image prompt.\n" +
  "The image should be as simple as possible.\nDon't include any chars like ` in the response " +
  "as they could complicate JSON serialization for later needs."

export const DEFAULT_EXPLANATION_IMAGE_PROMPT = "Do not create any details that are not mentioned below, " +
  "draw only what is explicitly mentioned.\nGenerate a minimalist 2D line drawing sketch that will be used as an " +
  "explanation for a math problem with minimum amount of colors and details:\n";

export const CHALLENGE_STATUS_OPTIONS = [
  { value: 'all', label: 'ALL' },
  { value: 'active', label: 'ACTIVE' },
  { value: 'inactive', label: 'INACTIVE' }
];

export const isOpenResponseSolutionEmpty = (value) => {
  return value === "" || value === "-";
};

export const MAX_OPEN_RESPONSE_DECIMAL_PLACES = 8;

const isValidNumber = (input) => {
  // Check for valid number pattern using regex (basic structure, no length limit yet)
  if (!/^(-?\d*\.?\d*)$/.test(input)) return false;

  // Remove the minus sign if present
  const isNegative = input.startsWith("-");
  const numStr = isNegative ? input.slice(1) : input; // Remove "-" for length check

  // Split into integer and decimal parts
  const parts = numStr.split(".");
  const integerPart = parts[0] || ""; // Ensure there's at least an empty string
  const decimalPart = parts[1] || ""; // Ensure there's at least an empty string

  // Ensure there's no more than one zero before the decimal point
  if (integerPart.match(/^0{2,}$/)) return false;

  // Total digits should not exceed 10
  if (integerPart.length + decimalPart.length > 10) return false;

  // Ensure decimal part has at most 8 digits
  return decimalPart.length <= MAX_OPEN_RESPONSE_DECIMAL_PLACES;
};

const isValidFraction = (input) => {
  // Allow a single slash fraction: optional leading minus, integer numerator and denominator, nonzero denominator
  const isNegative = input.startsWith("-");
  const unsigned = isNegative ? input.slice(1) : input;

  if (unsigned.includes("-")) return false;

  const slashIndex = unsigned.indexOf("/");
  if (slashIndex === -1) return false;
  // Only a single slash allowed
  if (unsigned.indexOf("/", slashIndex + 1) !== -1) return false;

  const numerator = unsigned.slice(0, slashIndex);
  const denominator = unsigned.slice(slashIndex + 1);

  // Numerator must be non-empty integer digits
  if (!/^\d+$/.test(numerator)) return false;
  // Numerator can be "0", but not "00", "012", etc.
  if (numerator.length > 1 && numerator.startsWith("0")) return false;
  if (numerator.length > 10) return false;

  // Allow trailing slash while typing (e.g. "3/" or "-3/")
  if (denominator === "") return true;

  // Denominator must be non-empty integer digits
  if (!/^\d+$/.test(denominator)) return false;
  // Nonzero denominator: cannot be 0 or start with 0 (e.g. "0", "00", "05")
  if (denominator.startsWith("0")) return false;
  if (denominator.length > 10) return false;

  // Total digits in fraction should not exceed 10
  return numerator.length + denominator.length <= 10;
};

const isValidMixedNumber = (input) => {
  // Allow one mixed-number form:
  // optional leading minus, whole number, one single space, then a fraction under existing fraction rules.
  // Allow a trailing space and a trailing slash while typing.
  // Keep the 10-digit total cap.

  // Must contain exactly one single space
  const spaceIndex = input.indexOf(" ");
  if (spaceIndex === -1) return false;
  if (input.indexOf(" ", spaceIndex + 1) !== -1) return false;

  const wholePart = input.slice(0, spaceIndex);
  const fracPart = input.slice(spaceIndex + 1);

  // Whole number: optional leading minus, followed by digits
  const isNegative = wholePart.startsWith("-");
  const wholeUnsigned = isNegative ? wholePart.slice(1) : wholePart;

  if (!/^\d+$/.test(wholeUnsigned)) return false;
  if (wholeUnsigned.length > 1 && wholeUnsigned.startsWith("0")) return false;

  let totalDigits = wholeUnsigned.length;
  if (totalDigits > 10) return false;

  // Allow trailing space while typing (e.g. "4 " or "-4 ")
  if (fracPart === "") return true;

  // Fraction part cannot have a minus sign
  if (fracPart.includes("-")) return false;

  const slashIndex = fracPart.indexOf("/");
  if (slashIndex === -1) {
    // Numerator only while typing (e.g. "4 1" or "-4 1")
    const numerator = fracPart;
    if (!/^\d+$/.test(numerator)) return false;
    if (numerator.length > 1 && numerator.startsWith("0")) return false;
    totalDigits += numerator.length;
    return totalDigits <= 10;
  }

  // Only a single slash allowed in fraction part
  if (fracPart.indexOf("/", slashIndex + 1) !== -1) return false;

  const numerator = fracPart.slice(0, slashIndex);
  const denominator = fracPart.slice(slashIndex + 1);

  if (!/^\d+$/.test(numerator)) return false;
  if (numerator.length > 1 && numerator.startsWith("0")) return false;
  totalDigits += numerator.length;
  if (totalDigits > 10) return false;

  // Allow trailing slash while typing (e.g. "4 1/" or "-4 1/")
  if (denominator === "") return true;

  if (!/^\d+$/.test(denominator)) return false;
  if (denominator.startsWith("0")) return false;
  totalDigits += denominator.length;

  return totalDigits <= 10;
};

export const validOpenResponseSolution = (input) => {
  /*
  Validate that the solution is a number, a single slash fraction, a mixed number, or a percentage:
  - Number: maximum 10 digits, optional decimal point, at most 8 digits after decimal
  - Fraction: optional leading minus, integer numerator and denominator, nonzero denominator, up to 10 total digits
  - Mixed number: optional leading minus, whole number, one single space, fraction, up to 10 total digits
  - Percentage: valid number followed by "%"

  Allow cases:
  - Empty string and minus sign, so the box can be cleared and negative numbers can be entered
  - Whole numbers ("123", "-12345"), up to 10 digits total
  - Numbers with decimal point ("123.45", "-98765.43210123"), up to 8 numbers after the decimal point
  - Numbers with a trailing dot ("123.", "-5678."), so the dot can be entered in the box in the first place
  - Single slash fractions ("3/5", "-3/5") and trailing slash ("3/") while typing
  - Mixed numbers ("4 1/125", "-2 3/4"), with trailing space ("4 ") or trailing slash ("4 1/") while typing
  - Percentages ("1.5%", "50%", "-10%")
  */
  if (isOpenResponseSolutionEmpty(input)) return true;

  if (input.includes(" ")) {
    return isValidMixedNumber(input);
  }

  if (input.endsWith("%")) {
    if (input.indexOf("%") !== input.length - 1) return false;
    const numPart = input.slice(0, -1);
    if (isOpenResponseSolutionEmpty(numPart)) return false;
    return isValidNumber(numPart);
  }

  if (input.includes("/")) {
    return isValidFraction(input);
  }

  return isValidNumber(input);
};

export const addLeadingZeroIfDot = (input) => {
  // Check if the input starts with a minus sign
  const isNegative = input.startsWith("-");

  // If it starts with a minus sign, remove it temporarily
  if (isNegative) {
    input = input.slice(1);
  }

  // If the input starts with a dot, add a leading zero
  if (input.startsWith(".")) {
    // Reattach the negative sign if the number was negative
    if (isNegative) {
      return "-0" + input;
    }
    return "0" + input;
  }

  // Remove all leading zeros in the integer part (before the decimal)
  input = input.replace(/^0+(?=\d)/, '');

  // Reattach the negative sign if the number was negative
  if (isNegative) {
    input = "-" + input;
  }

  return input; // Otherwise, return the input as is
};

export const removeTrailingZeros = (input) => {
  if (typeof input !== "string") return input;
  if (input.endsWith("%")) {
    return input.slice(0, -1).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') + "%";
  }
  // Remove trailing zeros after the decimal point and remove decimal point if it's the last character
  return input.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

export const parseFractionOrNumber = (s) => {
  if (s == null) return null;
  let str = String(s).trim();
  if (!str) return null;

  str = str.replace(/^-\s+/, "-");
  str = str.replace(/\s*\/\s*/g, "/");
  const parts = str.trim().split(/\s+/);

  if (parts.length === 2) {
    const [wholeStr, fracStr] = parts;
    if (!/^-?\d+$/.test(wholeStr) || !/^\d+\/\d+$/.test(fracStr)) {
      return null;
    }
    const [fracNumStr, fracDenStr] = fracStr.split("/");
    const fracDen = BigInt(fracDenStr);
    if (fracDen <= 0n) return null;
    const fracNum = BigInt(fracNumStr);
    const whole = BigInt(wholeStr);
    if (wholeStr.startsWith("-")) {
      return { num: whole * fracDen - fracNum, den: fracDen };
    }
    return { num: whole * fracDen + fracNum, den: fracDen };
  }

  if (parts.length === 1) {
    const part = parts[0];
    if (!/^-?\d+(?:\.\d+)?$/.test(part) && !/^-?\d+\/\d+$/.test(part)) {
      return null;
    }
    if (part.includes("/")) {
      const [numStr, denStr] = part.split("/");
      const den = BigInt(denStr);
      if (den <= 0n) return null;
      const num = BigInt(numStr);
      return { num, den };
    }
    // Integer or decimal
    const isNegative = part.startsWith("-");
    const unsigned = isNegative ? part.slice(1) : part;
    if (unsigned.includes(".")) {
      const [intPart, decPart] = unsigned.split(".");
      const scale = BigInt(decPart.length);
      const den = 10n ** scale;
      const unsignedNum = BigInt(intPart) * den + BigInt(decPart);
      const num = isNegative ? -unsignedNum : unsignedNum;
      return { num, den };
    } else {
      const num = BigInt(part);
      return { num, den: 1n };
    }
  }

  return null;
};

export const isCorrectOpenResponse = (studentSolution, correctSolution) => {
  if (studentSolution == null || correctSolution == null) {
    return false;
  }

  const a = String(studentSolution).trim();
  const e = String(correctSolution).trim();
  if (!a || !e) {
    return false;
  }

  if (a.toLowerCase() === e.toLowerCase()) {
    return true;
  }

  const eIsPct = e.endsWith("%");
  const aIsPct = a.endsWith("%");

  if (eIsPct) {
    const valE = parseFractionOrNumber(e.slice(0, -1));
    const valA = parseFractionOrNumber(aIsPct ? a.slice(0, -1) : a);
    if (valE !== null && valA !== null) {
      return valA.num * valE.den === valE.num * valA.den;
    }
    return false;
  }

  if (aIsPct) {
    return false;
  }

  const valA = parseFractionOrNumber(a);
  const valE = parseFractionOrNumber(e);

  if (valA !== null && valE !== null) {
    return valA.num * valE.den === valE.num * valA.den;
  }

  return a.toLowerCase() === e.toLowerCase();
};

export const replaceRemainingAstRTags = (prompt) => {
  // Replace all ast-r tags with ___, except for:
  // 1. The exact special format created by extractAnswerContent: <ast-r type="text" marker="1"></ast-r>
  // 2. Standalone ast-r tags that appear alone in a paragraph (these should remain as input fields)

  // First, identify standalone ast-r tags (those that appear alone in a paragraph with only whitespace)
  const standalonePattern = /<p[^>]*>\s*<ast-r[^>]*><\/ast-r>\s*<\/p>/g;
  const standaloneMatches = [...prompt.matchAll(standalonePattern)];

  // Replace all ast-r tags with ___, except the special format created by extractAnswerContent
  let result = prompt.replace(/<ast-r(?!\s+type="text"\s+marker="1"\s*><\/ast-r>)[^>]*><\/ast-r>/g, '___');

  // Restore standalone ast-r tags by replacing the ___ back to the original ast-r tag
  standaloneMatches.forEach(match => {
    const originalParagraph = match[0];
    const modifiedParagraph = originalParagraph.replace(/<ast-r[^>]*><\/ast-r>/, '___');
    if (result.includes(modifiedParagraph)) {
      result = result.replace(modifiedParagraph, originalParagraph);
    }
  });

  return result;
};

export const extractAnswerContent = (prompt) => {
  const containsHTML = /<[^>]*>/g.test(prompt);
  if (containsHTML) {
    // Look for content that comes after <ast-r> elements within a paragraph
    const astRAfterMatch = prompt.match(/<p[^>]*>\s*<ast-r[^>]*><\/ast-r>\s*(.+?)\s*<\/p>/);
    if (astRAfterMatch) {
      const contentText = astRAfterMatch[1].trim();
      if (contentText && !/^\d/.test(contentText) && !/^[A-Z]/.test(contentText)) {
        // Remove the entire paragraph containing the ast-r element and content
        const modifiedPrompt = prompt.replace(astRAfterMatch[0], `<p><ast-r type="text" marker="1"></ast-r></p>`);
        // Replace any remaining ast-r tags with ___
        const finalPrompt = replaceRemainingAstRTags(modifiedPrompt);
        return { prompt: finalPrompt, content: contentText, contentPosition: 'after' };
      }
    }

    // Look for content that comes before <ast-r> elements within a paragraph (e.g., $<ast-r>, <em>m</em> = <ast-r>)
    // Split by paragraphs and check each one individually to avoid cross-paragraph matching
    const paragraphs = prompt.split(/<\/p>/);
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i] + '</p>';
      if (paragraph.includes('<ast-r')) {
        // Look for content immediately before an ast-r tag (no other ast-r tags in between)
        const astRBeforeMatch = paragraph.match(/<p[^>]*>\s*([^<]*(?:<(?!ast-r)[^>]*>[^<]*)*)\s*<ast-r[^>]*><\/ast-r>\s*<\/p>/);
        if (astRBeforeMatch) {
          const contentText = astRBeforeMatch[1].trim();
          // For content before ast-r, be more lenient with validation - allow content that starts with capital letters
          // but still exclude content that starts with digits
          if (contentText && !/^\d/.test(contentText)) {
            // Remove the entire paragraph containing the content and ast-r element
            const modifiedPrompt = prompt.replace(astRBeforeMatch[0], `<p><ast-r type="text" marker="1"></ast-r></p>`);
            // Replace any remaining ast-r tags with ___
            const finalPrompt = replaceRemainingAstRTags(modifiedPrompt);
            return { prompt: finalPrompt, content: contentText, contentPosition: 'before' };
          }
        }
      }
    }
    // No extractable content found, but replace all ast-r tags with ___
    const finalPrompt = replaceRemainingAstRTags(prompt);
    return { prompt: finalPrompt };
  }

  // Original logic for non-HTML content
  // Find the last occurrence of '.', '?', or '!'
  const regex = /([?!.])[^?!.]*$/;
  const lastPunctuationMatch = prompt.match(regex);
  if (lastPunctuationMatch) {
    const lastPunctuationIndex = prompt.lastIndexOf(lastPunctuationMatch[1]); // Get the last punctuation position
    let extractedContent = prompt.slice(lastPunctuationIndex + 1).trim(); // Extract words after punctuation
    let modifiedPrompt = prompt.slice(0, lastPunctuationIndex + 1); // Keep the sentence without extracted text
    // Special case: Extract content like "ft." if it's the last word after an equation
    const equationMatch = prompt.match(/=\s*([\w$%.]+)/);
    if (equationMatch && !/^\d/.test(equationMatch[1]) && !/^[A-Z]/.test(equationMatch[1])) {
      extractedContent = equationMatch[1];
      modifiedPrompt = prompt.replace(new RegExp(`\\s*${equationMatch[1]}$`), ""); // Remove extracted content
    }
    // If the extracted part starts with a digit or a capital letter, it is ignored
    if (extractedContent && !/^\d/.test(extractedContent) && !/^[A-Z]/.test(extractedContent)) {
      return { prompt: modifiedPrompt, content: extractedContent };
    }
  }
  return { prompt: prompt };
};

// Backward compatibility - keep the old function name as an alias
export const removeUnits = extractAnswerContent;
